import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { H, MANROPE, BandChip, FLAG_LABELS, ddmon } from "../../healthcheck/hcTokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/health-check`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: H.ink3 };

const Card = ({ title, children, testid }) => (
  <div className="rounded-[12px] overflow-hidden" data-testid={testid}
    style={{ background: H.card, border: `1px solid ${H.border}` }}>
    <div className="px-4 py-2.5" style={{ background: H.sunken, borderBottom: `1px solid ${H.divider}` }}>
      <div style={LABEL}>{title}</div>
    </div>
    <div className="px-4 py-3.5">{children}</div>
  </div>
);

const Row = ({ q, children }) => (
  <div className="py-2.5" style={{ borderBottom: `1px solid ${H.divider}` }}>
    <div className="text-[12px]" style={{ color: H.ink3 }}>{q}</div>
    <div className="text-[13.5px] mt-0.5 whitespace-pre-wrap" style={{ color: H.ink }}>
      {children === null || children === undefined || children === "" ? "—" : children}
    </div>
  </div>
);

export default function HealthCheckSubmissionPage({ recordId }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/submissions/${encodeURIComponent(recordId)}`)
      .then((r) => setS(r.data))
      .catch(() => toast.error("Could not load that submission"));
  }, [recordId]);

  if (!s) return <div className="px-2 py-6 text-[13px]" style={{ color: H.ink2 }}>Loading…</div>;
  const a = s.answers || {};
  const feats = (s.meta?.features || []).filter((f) => (a.A7 || []).includes(f.product));
  const bk = s.breakdown || {};

  return (
    <div className="pb-8" data-testid="hc-submission-page">
      <div className="text-[11.5px] mb-2.5">
        <a href="/customer-success/health-checks" style={{ color: H.accentInk }}>Health Checks</a>
        <span style={{ color: H.ink3 }}> / </span>
        <span style={{ color: H.ink, fontWeight: 600 }}>{s.company_name}</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, letterSpacing: "-0.018em", color: H.ink }}
            data-testid="hc-sub-title">{s.company_name}</h1>
          <div className="text-[13px] mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: H.ink2 }}>
            <a href={`/clients/${s.client_id}`} className="mono text-[11.5px]" style={{ color: H.accentInk }}>{s.client_id}</a>
            <span style={{ color: H.ink3 }}>·</span>
            <span>{s.mode === "client" ? "Completed by the client" : "Completed on a call"}</span>
            <span style={{ color: H.ink3 }}>·</span>
            <span style={{ fontWeight: 600, color: H.ink }}>{s.completed_by}</span>
            <span style={{ color: H.ink3 }}>·</span>
            <span>{ddmon(s.submitted_at)}</span>
            <span style={{ color: H.ink3 }}>·</span>
            <span>Tier {s.tier}</span>
            <span style={{ color: H.ink3 }}>·</span>
            <span>Scoring v{s.scoring_version}</span>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div>
            <div style={LABEL}>Score</div>
            <div className="mono" data-testid="hc-sub-score"
              style={{ fontSize: 30, fontWeight: 800, color: H.ink, lineHeight: 1.1 }}>
              {s.score}<span className="text-[15px]" style={{ color: H.ink3 }}>/100</span>
            </div>
          </div>
          <div>
            <div style={LABEL}>Calculated</div>
            <div className="mt-1"><BandChip band={s.calculated_band} testid="hc-sub-calc" /></div>
          </div>
          <div>
            <div style={LABEL}>Final</div>
            <div className="mt-1"><BandChip band={s.final_band} testid="hc-sub-final" /></div>
          </div>
        </div>
      </div>

      {s.override_reason && (
        <div className="rounded-[9px] px-4 py-3 mb-4 text-[13px]" data-testid="hc-sub-override"
          style={{ background: "#FFF7D1", border: "1px solid #E8D48A", color: "#6B5600" }}>
          <b>Band overridden by staff.</b> {s.override_reason} — the calculated score and band are
          kept exactly as they were.
        </div>
      )}

      <div className="grid gap-3.5 items-start grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="space-y-3.5 min-w-0">
          <Card title="About you" testid="hc-sub-a">
            <Row q="Your name">{a.A2}</Row>
            <Row q="Your role">{a.A3}</Row>
            <Row q="Email">{a.A4}</Row>
            <Row q="Phone">{a.A5}</Row>
            <Row q="Which best describes your business?">{a.A6}</Row>
            <Row q="Products used">{(a.A7 || []).join(", ")}</Row>
          </Card>

          <Card title="Satisfaction" testid="hc-sub-b">
            <Row q="Satisfaction with the software">{a.B1 ? `${a.B1} / 5` : null}</Row>
            <Row q="Satisfaction with service and support">{a.B2 ? `${a.B2} / 5` : null}</Row>
            <Row q="Likely to recommend">{a.B3 !== undefined ? `${a.B3} / 10` : null}</Row>
            <Row q="One thing we could do better">{a.B4}</Row>
          </Card>

          <Card title="Pain points" testid="hc-sub-c">
            {(s.pain_points || []).length === 0 ? (
              <div className="text-[13px] py-1" style={{ color: H.ink2 }}>None raised.</div>
            ) : s.pain_points.map((p, i) => (
              <div key={i} className="py-2.5" style={{ borderBottom: `1px solid ${H.divider}` }}>
                <div className="text-[13.5px]" style={{ color: H.ink }}>{p.text}</div>
                <div className="text-[12px] mt-0.5" style={{ color: H.ink3 }}>
                  {p.product || "Not stated"} · severity {p.severity}
                  {p.workaround ? ` · works around it by: ${p.workaround}` : ""}
                </div>
              </div>
            ))}
            <Row q="Still using whiteboards, spreadsheets or other systems">{a.C5}</Row>
          </Card>

          {feats.length > 0 && (
            <Card title="Product and features" testid="hc-sub-d">
              {feats.map((f) => (
                <Row key={f.id} q={f.label}>
                  {(s.meta?.feature_answers || {})[(a.features || {})[f.id]] || "Not answered"}
                  {(a.walkthrough || {})[f.id] ? " · wants a 15-minute walkthrough" : ""}
                </Row>
              ))}
              <Row q="Anything you wish our software did">{a.D6}</Row>
            </Card>
          )}

          {(s.meta?.discover_features || []).length > 0 && (
            <Card title="Did you know?" testid="hc-sub-dk">
              {(s.meta?.discover_features || []).map((f) => (
                <Row key={f.id} q={f.label}>
                  {(s.meta?.feature_answers || {})[(a.discover || {})[f.id]] || "Not answered"}
                  {(a.discover_walkthrough || {})[f.id] ? " · wants a 15-minute walkthrough" : ""}
                </Row>
              ))}
            </Card>
          )}

          <Card title="Looking ahead" testid="hc-sub-e">
            <Row q="What's changing in the next 6 to 12 months">
              {[(a.E1 || []).join(", "), a.E1_other].filter(Boolean).join(" · ")}
            </Row>
            <Row q="Looking at other software or building their own">{a.E2}</Row>
            <Row q="Change in who manages or uses EQUIPR">{a.E3}</Row>
          </Card>

          {s.mode === "client" ? (
            <Card title="Follow-up" testid="hc-sub-f">
              <Row q="Would like a call">{a.F1}</Row>
              <Row q="Best time to reach them">{a.F2}</Row>
              <Row q="Privacy consent">{a.F3 ? "Given" : "Not given"}</Row>
            </Card>
          ) : (
            <Card title="Staff section" testid="hc-sub-g">
              <Row q="Completed by">{a.G1}</Row>
              <Row q="Call date">{ddmon(a.G2)}</Row>
              <Row q="Tier">{a.G3}</Row>
              <Row q="Staff observations">{a.G4}</Row>
              <Row q="Our commitment">{a.G5}</Row>
              <Row q="Commitment owner">{a.G6}</Row>
              <Row q="Commitment due">{ddmon(a.G7)}</Row>
              <Row q="Override">{a.G8 || "No override"}</Row>
            </Card>
          )}
        </div>

        <div className="space-y-3.5 min-w-0">
          <Card title="Score breakdown" testid="hc-sub-breakdown">
            {[["Software satisfaction", bk.software_satisfaction, 25],
              ["Service satisfaction", bk.service_satisfaction, 25],
              ["Likely to recommend", bk.recommend, 20],
              ["Pain points", bk.pain_points, 20],
              ["Feature awareness", bk.feature_awareness, 10]].map(([l, v, max]) => (
              <div key={l} className="flex items-center justify-between py-1.5"
                style={{ borderBottom: `1px solid ${H.divider}` }}>
                <span className="text-[13px]" style={{ color: H.ink2 }}>{l}</span>
                <span className="mono text-[13px] font-bold" style={{ color: H.ink }}>
                  {v ?? 0}<span style={{ color: H.ink3 }}> / {max}</span>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <span className="text-[13px] font-bold" style={{ color: H.ink }}>Total</span>
              <span className="mono text-[14px] font-bold" style={{ color: H.ink }}>{s.score} / 100</span>
            </div>
          </Card>

          <Card title="Flags" testid="hc-sub-flags">
            {(s.flags || []).length === 0 ? (
              <div className="text-[13px]" style={{ color: H.ink2 }}>None raised.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {s.flags.map((f) => (
                  <span key={f} className="text-[11.5px] px-2 py-0.5 rounded-full"
                    style={{ background: H.sunken, border: `1px solid ${H.divider}`, color: H.ink2 }}>
                    {FLAG_LABELS[f] || f}
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card title="What this created" testid="hc-sub-created">
            {(s.todos || []).length === 0 && (s.backlog || []).length === 0 ? (
              <div className="text-[13px]" style={{ color: H.ink2 }}>Nothing needed.</div>
            ) : (<>
              {(s.todos || []).map((t) => (
                <div key={t.id} className="py-2" style={{ borderBottom: `1px solid ${H.divider}` }}>
                  <div className="text-[13px]" style={{ color: H.ink }}>{t.title}</div>
                  <div className="text-[12px] mt-0.5" style={{ color: H.ink3 }}>
                    {t.owner_name || "Unassigned"} · due {ddmon(t.due_date)} · {t.status === "done" ? "done" : "open"}
                  </div>
                </div>
              ))}
              {(s.backlog || []).map((b) => (
                <div key={b.id} className="py-2" style={{ borderBottom: `1px solid ${H.divider}` }}>
                  <div className="text-[13px]" style={{ color: H.ink }}>
                    {b.kind === "feature_request" ? "Feature ask" : "Pain point"} — {b.text}
                  </div>
                  <div className="text-[12px] mt-0.5" style={{ color: H.ink3 }}>
                    Answer due {ddmon(b.answer_due)}
                  </div>
                </div>
              ))}
            </>)}
          </Card>
        </div>
      </div>
    </div>
  );
}
